import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Languages, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { STATES } from "@/lib/states";

export default function Register() {
  const { setUser } = useAuth();
  const { t, toggle } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ badge_number: "", name: "", designation: "Constable", state: "Delhi", password: "" });
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password must be at least 8 characters with uppercase, lowercase and a digit.");
    if (form.password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      localStorage.setItem("token", data.token);
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1760872645513-63b6846ce3c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/emblem-gold.png" alt="National Emblem of India" className="h-14 w-auto object-contain shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{t("govOfIndia")} · {t("mha")}</div>
            <div className="font-head font-bold text-lg leading-tight">{t("appName")}</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-3xl font-bold leading-tight mb-4">Create your<br />training account</h1>
          <p className="text-slate-300 max-w-md">Register with your badge number to start practising 1930 / NCRP call handling in a safe, synthetic sandbox.</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
            <ShieldCheck className="h-4 w-4" /> Internal training use only · Synthetic data
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© I4C Training Simulator · Pilot</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-login">
              <ArrowLeft className="h-4 w-4" /> {t("login")}
            </Link>
            <button onClick={toggle} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Languages className="h-4 w-4" /> {t("lang")}
            </button>
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-900">Register</h2>
          <p className="text-slate-500 mb-6 text-sm">Create a trainee account</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="badge">{t("badgeNumber")}</Label>
              <Input id="badge" data-testid="reg-badge" value={form.badge_number} onChange={(e) => set("badge_number", e.target.value)} placeholder="e.g. TRN2024" className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="name">{t("name")}</Label>
              <Input id="name" data-testid="reg-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("designation")}</Label>
                <Input id="designation" data-testid="reg-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="e.g. Constable, Inspector" className="mt-1.5" />
              </div>
              <div>
                <Label>{t("state")}</Label>
                <select value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="reg-state">
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="pw">{t("password")}</Label>
              <Input id="pw" data-testid="reg-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" required />
              <p className="text-xs text-slate-400 mt-1">Min 8 chars, uppercase, lowercase &amp; digit required</p>
            </div>
            <div>
              <Label htmlFor="cpw">Confirm {t("password")}</Label>
              <Input id="cpw" data-testid="reg-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" required />
            </div>
            {error && <div data-testid="reg-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-slate-800 h-11">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />...</> : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already registered? <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid="go-login">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
